import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, deleteField, Timestamp, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config';
import type { Habit, HabitCategory, HabitFrequencyConfig } from '@/types/habits';
import { habitBadges } from '@/lib/constants/habitBadges';
import { startOfDay, isToday, format } from 'date-fns';
import { handleFirebaseError } from '../utils';
import {
  daysSinceStart,
  habitLogId,
  localDateKey,
  nextHabitLogState,
  streakBefore,
  targetCountOf,
  type ExistingHabitLog,
  type HabitLogAction,
} from '@/lib/habits/habitLogState';

// Check if habit is logged today by querying habitLogs collection
// Returns the number of times logged today
export async function getHabitCompletionCountToday(habitId: string, userId: string): Promise<number> {
  try {
    const today = format(new Date(), 'yyyy-MM-dd');
    const logId = `${userId}_${habitId}_${today}`;
    const logRef = doc(db, 'habitLogs', logId);
    const logSnap = await getDoc(logRef);
    
    if (!logSnap.exists()) return 0;
    return logSnap.data()?.completionCount || (logSnap.data()?.completed ? 1 : 0);
  } catch (error) {
    console.error('Error checking habit completion count:', error);
    return 0;
  }
}

// Check if habit is logged today (backward compatible)
export async function isHabitLoggedToday(habitId: string, userId: string): Promise<boolean> {
  try {
    const today = format(new Date(), 'yyyy-MM-dd');
    const logId = `${userId}_${habitId}_${today}`;
    const logRef = doc(db, 'habitLogs', logId);
    const logSnap = await getDoc(logRef);
    
    return logSnap.exists() && logSnap.data()?.completed === true;
  } catch (error) {
    console.error('Error checking if habit is logged today:', error);
    return false;
  }
}

// Get completion count for a specific date
export async function getHabitCompletionCountOnDate(habitId: string, userId: string, date: Date): Promise<number> {
  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    const logId = `${userId}_${habitId}_${dateStr}`;
    const logRef = doc(db, 'habitLogs', logId);
    const logSnap = await getDoc(logRef);
    
    if (!logSnap.exists()) return 0;
    return logSnap.data()?.completionCount || (logSnap.data()?.completed ? 1 : 0);
  } catch (error) {
    console.error('Error checking habit completion count on date:', error);
    return 0;
  }
}

// Check if habit is logged on a specific date
export async function isHabitLoggedOnDate(habitId: string, userId: string, date: Date): Promise<boolean> {
  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    const logId = `${userId}_${habitId}_${dateStr}`;
    const logRef = doc(db, 'habitLogs', logId);
    const logSnap = await getDoc(logRef);
    
    return logSnap.exists() && logSnap.data()?.completed === true;
  } catch (error) {
    console.error('Error checking if habit is logged on date:', error);
    return false;
  }
}

// Get habit logs for a specific habit to calculate streak and progress
export async function getHabitLogs(habitId: string, userId: string, days: number = 90): Promise<any[]> {
  try {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const startDateStr = format(startDate, 'yyyy-MM-dd');
    const endDateStr = format(endDate, 'yyyy-MM-dd');
    
    const logsQuery = query(
      collection(db, 'habitLogs'),
      where('userId', '==', userId),
      where('habitId', '==', habitId),
      where('date', '>=', startDateStr),
      where('date', '<=', endDateStr)
    );
    
    const snapshot = await getDocs(logsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting habit logs:', error);
    return [];
  }
}

export async function getUserHabits(userId: string): Promise<Habit[]> {
  try {
    const habitsQuery = query(
      collection(db, 'habits'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(habitsQuery);
    
    const habits = await Promise.all(snapshot.docs.map(async doc => {
      const data = doc.data();
      
      // Default frequency config for backward compatibility
      const frequencyConfig = data.frequencyConfig || {
        frequency: 'daily',
        timesPerFrequency: 1,
        duration: 90,
      };
      
      const duration = frequencyConfig.duration;
      
      // Get logs from habitLogs collection to calculate real progress and streak
      const logs = await getHabitLogs(doc.id, userId, duration);
      const completedLogs = logs.filter(log => log.completed);
      
      // Calculate streak from logs
      let streak = 0;
      const today = new Date();
      for (let i = 0; i < duration; i++) {
        const checkDate = new Date(today);
        checkDate.setDate(checkDate.getDate() - i);
        const dateStr = format(checkDate, 'yyyy-MM-dd');
        
        const logForDate = logs.find(log => log.date === dateStr);
        if (logForDate && logForDate.completed) {
          streak++;
        } else {
          break;
        }
      }
      
      // Create history array from logs
      const history = Array(duration).fill(false);
      const startDate = new Date(data.createdAt?.toDate() || new Date());
      
      logs.forEach(log => {
        const logDate = new Date(log.date);
        const dayIndex = Math.floor((logDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        if (dayIndex >= 0 && dayIndex < duration) {
          history[dayIndex] = log.completed;
        }
      });
      
      const daysCompleted = completedLogs.length;
      const progress = Math.min(100, Math.round((daysCompleted * 100) / duration));
      
      // Update badge level based on days completed
      let currentBadgeLevel = data.currentBadgeLevel || 'bronze';
      if (daysCompleted >= duration) currentBadgeLevel = 'gold';
      else if (daysCompleted >= duration * 0.66) currentBadgeLevel = 'silver';
      else if (daysCompleted >= duration * 0.33) currentBadgeLevel = 'bronze';
      
      return {
        id: doc.id,
        ...data,
        history,
        progress,
        streak,
        daysCompleted,
        currentBadgeLevel,
        frequencyConfig,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastLoggedAt: completedLogs.length > 0 ? new Date(completedLogs[completedLogs.length - 1].timestamp) : null
      } as Habit;
    }));
    
    return habits;
  } catch (error) {
    console.error('Error getting user habits:', error);
    return [];
  }
}

export async function createHabit(data: Partial<Habit>) {
  try {
    const now = Timestamp.now();
    
    // Default frequency config if not provided
    const defaultFrequencyConfig = {
      frequency: 'daily' as const,
      timesPerFrequency: 1,
      duration: 90,
    };
    
    const habitData: Record<string, unknown> = {
      ...data,
      currentBadgeLevel: 'bronze',
      isPrivate: data.isPrivate || false,
      frequencyConfig: data.frequencyConfig || defaultFrequencyConfig,
      createdAt: now,
      updatedAt: now
    };

    // Firestore rejects a literal `undefined` in a write. Strip any key left
    // undefined (e.g. a caller that passes `stepsTarget: undefined` for "no
    // target") so the key is omitted entirely rather than failing the write.
    for (const key of Object.keys(habitData)) {
      if (habitData[key] === undefined) delete habitData[key];
    }

    const docRef = await addDoc(collection(db, 'habits'), habitData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating habit:', error);
    throw error;
  }
}

export async function updateHabit(habitId: string, data: Partial<Habit>) {
  try {
    const habitRef = doc(db, 'habits', habitId);
    const updateData: Record<string, unknown> = {
      ...data,
      progress: Math.round(data.progress || 0),
      updatedAt: Timestamp.now()
    };

    // Present-and-undefined means "unlink". updateDoc merges, so omitting the
    // key would leave the old target stored and the habit would keep
    // auto-completing on mobile after the user believed they'd unlinked it.
    // Keyed on the key's PRESENCE so a caller that never mentions stepsTarget
    // cannot strip one someone else set.
    if ('stepsTarget' in data && data.stepsTarget === undefined) {
      updateData.stepsTarget = deleteField();
    }

    await updateDoc(habitRef, updateData);
    return true;
  } catch (error) {
    console.error('Error updating habit:', error);
    throw error;
  }
}

export async function deleteHabit(habitId: string) {
  try {
    const habitRef = doc(db, 'habits', habitId);
    await deleteDoc(habitRef);
    return true;
  } catch (error) {
    console.error('Error deleting habit:', error);
    throw error;
  }
}

// Today's personal log for one habit. The next document is decided by the
// shared, pure nextHabitLogState (src/lib/habits/habitLogState.ts — a
// byte-identical copy lives in namat-mobile), so a tap on either client
// writes the same document (namat#36 P1-1).
async function applyHabitLogAction(habitId: string, userId: string | undefined, action: HabitLogAction) {
  const habitRef = doc(db, 'habits', habitId);
  const habitSnap = await getDoc(habitRef);

  if (!habitSnap.exists()) {
    throw new Error('Habit not found');
  }

  const habitData = habitSnap.data();
  const actualUserId = userId || habitData.userId;
  const frequencyConfig = habitData.frequencyConfig || { frequency: 'daily', timesPerFrequency: 1, duration: 90 };

  const now = new Date();
  const date = localDateKey(now);
  const logRef = doc(db, 'habitLogs', habitLogId(actualUserId, habitId, date));
  const logSnap = await getDoc(logRef);
  const existing = logSnap.exists() ? (logSnap.data() as ExistingHabitLog) : null;

  // The streak and start-day fields only go into a full write, so skip the
  // history read on an unlog.
  let priorStreak = 0;
  let startDays = 0;
  if (action !== 'unlog') {
    const createdAt = habitData.createdAt && typeof habitData.createdAt.toDate === 'function'
      ? habitData.createdAt.toDate()
      : new Date(habitData.createdAt);
    startDays = daysSinceStart(createdAt, now);
    const duration = frequencyConfig.duration || 90;
    priorStreak = streakBefore(await getHabitLogs(habitId, actualUserId, duration), now, duration);
  }

  const transition = nextHabitLogState(existing, action, {
    userId: actualUserId,
    habitId,
    date,
    targetCount: targetCountOf(habitData),
    priorStreak,
    daysSinceStart: startDays,
    now,
    habitName: habitData.name,
    habitCategory: habitData.category,
    frequencyType: frequencyConfig.frequency,
    source: 'manual',
  });

  switch (transition.type) {
    case 'reject':
      throw new Error(transition.reason);
    case 'delete':
      await deleteDoc(logRef);
      break;
    case 'update':
      await updateDoc(logRef, transition.data);
      break;
    case 'set':
      await setDoc(logRef, transition.data);
      break;
  }
  return true;
}

export async function logHabit(habitId: string, userId?: string) {
  try {
    return await applyHabitLogAction(habitId, userId, 'log');
  } catch (error) {
    console.error('Error logging habit:', error);
    throw error;
  }
}

export async function unlogHabit(habitId: string, userId?: string) {
  try {
    return await applyHabitLogAction(habitId, userId, 'unlog');
  } catch (error) {
    console.error('Error unlogging habit:', error);
    throw error;
  }
}

// Bulk delete habit log entries (for admin logs management)
export async function bulkDeleteHabitLogEntries(logIds: string[]) {
  try {
    const deletePromises = logIds.map(logId => {
      const logRef = doc(db, 'habitLogs', logId);
      return deleteDoc(logRef);
    });
    
    await Promise.all(deletePromises);
    return true;
  } catch (error) {
    console.error('Error bulk deleting habit log entries:', error);
    throw error;
  }
}

// Get display name for habit (handles private habits)
export function getHabitDisplayName(habit: Habit, currentUserId?: string | null, t?: (key: string) => string): string {
  // If habit is private and current user is not the owner, show generic name
  if (habit.isPrivate && currentUserId && habit.userId !== currentUserId) {
    return t ? t('habits.privateHabit') : 'Private Habit';
  }
  
  // Otherwise show the actual habit name
  return habit.name;
}